import { ChangeEvent, ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Modal, { useModalProps, useModalState } from 'react-simple-modal-provider';
import { useLoadingStatusContext } from '../../context';
import { form } from '../../modules/team/common/add-user-form';
import { WorkspaceMember } from '../../modules/team/imports/types';
import { useAppSelector } from '../../redux/hooks';
import Button from '../button/Button';
import Checkbox from '../checkbox/Checkbox';
import Field from '../field/Field';
import Form from '../form/Form';
import Input from '../input/Input';
import ModalLayout from '../layouts/ModalLayout';
import Select from '../select/Select';
import TeamRoleSelect from '../selectDropdown/TeamRoleSelect';
import Typography from '../typography/Typography';

type AddUserModalProps = {
  children: ReactNode;
};

const ModalBody = ({ setOpen, isOpen }: any) => {
  const { t } = useTranslation(['team']);
  const {
    wallet: { address },
  } = useAppSelector((state) => state.user);
  const { state } = useLoadingStatusContext();
  const { groupName, groupId, handleAddUserToGroup, members, groupMembers } =
    useModalProps('AddUserToGroupModal');

  const membersAndGroupMembersOptions: {
    value: any;
    label: string;
    disabled?: boolean | undefined;
    bold?: boolean | undefined;
    isHidden?: boolean | undefined;
  }[] = useMemo(() => {
    const membersOptions = members
      .filter(() => members.address !== address)
      .map((member: WorkspaceMember) => ({
        value: JSON.stringify({ name: member.name, email: member.email }),
        label: member.name,
        disabled: false,
        bold: false,
        isHidden: false,
      }));
    const groupMembersOptions = groupMembers.map((member: WorkspaceMember) => ({
      value: JSON.stringify({ name: member.name, email: member.email }),
      label: `${member.name} - G`,
      disabled: false,
      bold: true,
      isHidden: false,
    }));
    return [{ value: '', label: '' }, ...membersOptions, ...groupMembersOptions];
  }, [members, groupMembers]);

  return (
    <ModalLayout
      open={isOpen}
      onClose={() => {
        setOpen(false);
      }}
    >
      <div className="mb-16 mt-[20px] flex flex-col justify-start gap-[16px] px-[50px] pt-[60px]">
        <section className="flex h-full flex-col items-start ">
          <Typography
            weight="medium"
            size="body-medium-30"
            color="primary-500"
            className="mb-[10px]"
          >
            {t('manage.add_user.title')}
          </Typography>
          <Typography
            weight="medium"
            size="body-medium-20"
            color="primary-500"
            className="mb-[10px]"
          >
            {groupName}
          </Typography>
          <Form
            initialValues={form.initialValues}
            validationSchema={form.validationSchema}
            className="size-full py-5"
          >
            {({ fields, handleSubmit, errors, setValue, resetField, watch, isValid }) => {
              const { role, name, email, groupId: insideGroupId } = watch();
              const { groupId } = useModalProps('AddUserToGroupModal');

              const handleMemberSelect = (
                e:
                  | ChangeEvent<HTMLInputElement>
                  | ChangeEvent<HTMLTextAreaElement>
                  | ChangeEvent<HTMLSelectElement>
              ) => {
                const value = e.target.value;
                if (value) {
                  setValue('name', JSON.parse(value).name);
                  setValue('email', JSON.parse(value).email);
                } else {
                  resetField('name');
                  resetField('email');
                }
              };

              useEffect(() => {
                setValue('groupId', groupId);
              }, [groupId]);

              return (
                <div className="flex h-full flex-col justify-between">
                  <div className="flex w-full flex-col">
                    <Field error={errors.name?.message} label={t('manage.add_user.team_members')}>
                      <Select
                        onChange={handleMemberSelect}
                        options={membersAndGroupMembersOptions}
                      />
                    </Field>
                    <Field error={errors.name?.message} label={t('manage.add_user.name')}>
                      <Input
                        placeholder={t('manage.add_user.name-placeholder')}
                        name={fields.name.name}
                        id={fields.name.name}
                        onChange={fields.name.onChange}
                        onBlur={fields.name.onBlur}
                        inputRef={fields.name.ref}
                        className="mt-[5px] "
                      />
                    </Field>
                    <Field
                      error={errors.email?.message}
                      label={t('manage.add_user.email')}
                      className="mt-[10px]"
                    >
                      <Input
                        placeholder={t('manage.add_user.email-placeholder')}
                        name={fields.email.name}
                        id={fields.email.name}
                        onChange={fields.email.onChange}
                        onBlur={fields.email.onBlur}
                        inputRef={fields.email.ref}
                        className="mt-[5px]"
                      />
                    </Field>
                    <div className="mt-[5px] flex w-full items-center justify-start gap-x-2">
                      <Checkbox
                        name={fields.isGroupAdmin.name}
                        id={fields.isGroupAdmin.name}
                        onChange={() => {
                          setValue(fields.isGroupAdmin.name, !watch(fields.isGroupAdmin.name));
                        }}
                      />
                      <Typography size="md" weight="regular">
                        {t('manage.add_user.is_group_admin')}
                      </Typography>
                    </div>
                    <Typography weight="medium" size="body-medium-18" className="mb-1 mt-5">
                      {t('manage.permissions')}
                    </Typography>
                    {/* TOKEN_CREATOR */}
                    <div className="flex justify-between gap-6">
                      <Field label={t('manage.add_user.role.token_creator')} className="w-full">
                        <TeamRoleSelect
                          onChange={(selection: string) => {
                            if (selection === 'viewer') {
                              setValue('role.token_creator.write', false);
                              setValue('role.token_creator.read', true);
                            }
                            if (selection === 'editor') {
                              setValue('role.token_creator.write', true);
                              setValue('role.token_creator.read', true);
                            }
                            if (selection === 'no_access') {
                              setValue('role.token_creator.write', false);
                              setValue('role.token_creator.read', false);
                            }
                          }}
                          className="mb-1 mt-[5px] "
                        />
                      </Field>

                      {/* NOTARIZATION */}
                      <Field label={t('manage.add_user.role.notarization')}>
                        <TeamRoleSelect
                          onChange={(selection: string) => {
                            if (selection === 'viewer') {
                              setValue('role.notarization.write', false);
                              setValue('role.notarization.read', true);
                            }
                            if (selection === 'editor') {
                              setValue('role.notarization.write', true);
                              setValue('role.notarization.read', true);
                            }
                            if (selection === 'no_access') {
                              setValue('role.notarization.write', false);
                              setValue('role.notarization.read', false);
                            }
                          }}
                          className="mb-1 mt-[5px]"
                        />
                      </Field>

                      {/* AUDIT */}
                      <Field label={t('manage.add_user.role.audit')}>
                        <TeamRoleSelect
                          onChange={(selection: string) => {
                            if (selection === 'viewer') {
                              setValue('role.audit.write', false);
                              setValue('role.audit.read', true);
                            }
                            if (selection === 'editor') {
                              setValue('role.audit.write', true);
                              setValue('role.audit.read', true);
                            }
                            if (selection === 'no_access') {
                              setValue('role.audit.write', false);
                              setValue('role.audit.read', false);
                            }
                          }}
                          className="mt-[5px] "
                        />
                      </Field>
                    </div>
                  </div>

                  <div className="mt-7 flex items-end justify-end space-y-[25px]">
                    <Button
                      disabled={
                        !isValid ||
                        state.status === 'pending' ||
                        !Object.keys(role).some((module) => role[module].write || role[module].read)
                      }
                      action={() => {
                        handleSubmit(handleAddUserToGroup)().finally(() => {});
                      }}
                    >
                      {t('manage.add_user.title')}
                    </Button>
                  </div>
                </div>
              );
            }}
          </Form>
        </section>
      </div>
    </ModalLayout>
  );
};

const AddUserToGroupModal = ({ children }: AddUserModalProps) => {
  const [isOpen, setOpen] = useModalState();
  const { t } = useTranslation();
  return (
    <Modal id="AddUserToGroupModal" consumer={children} isOpen={isOpen} setOpen={setOpen}>
      <ModalBody setOpen={setOpen} isOpen={isOpen}></ModalBody>
    </Modal>
  );
};

export default AddUserToGroupModal;
